/* globals document */
import React, { MouseEventHandler, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ClassNames } from '@emotion/react'
import { dataProps, onProps, useUniqueId } from '@ds/react-utils'
import {
  IconOrImage,
  iconWithImageKinds,
} from '../../internal/components/IconOrImage'
import { InputRequiredAsterisk } from '../../internal/components/InputRequiredAsterisk'
import { consoleWarn, deprecatedPropMessage } from '../../logging'
import { CustomPropTypes } from '../../support'
import { useThemeStyles } from '../../theming'
import type {
  AriaAttributes,
  EventListenerPropsOmittingOnChange,
  IconPosition,
  InputOnChangeHandler,
  LabelForwardRef,
  OliveImageIcon,
  SystemIconKey,
} from '../../types'
import { addCssClass, removeCssClass } from '../../utilities'
import { keyboardEventKeys } from '../../variables'
import { InputDescription } from '../InputDescription'
import { TextEllipsisProps } from '../TextEllipsis'
import styles from './styles'
import { useFormContext } from '../FormContext/FormContext'

export const checkboxIcons = iconWithImageKinds
export const checkboxIconPositions = ['beforeText', 'afterText'] as const

export interface CheckboxProps
  extends EventListenerPropsOmittingOnChange<HTMLLabelElement>,
    AriaAttributes {
  /**
   * Indicates that the Checkbox is checked.
   */
  checked?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Applies a 'description' treatment, displaying the provided text.
   */
  description?: string
  /**
   * Applies a 'disabled' visual treatment.
   */
  disabled?: boolean
  /**
   * The provided component will display at the end of the `Checkbox`.
   */
  endElement?: React.ReactElement
  /**
   * Applies an 'error' treatment, displaying the provided text.
   */
  error?: string
  /**
   * A React ref to assign to the HTML label element.
   */
  forwardedRef?: LabelForwardRef
  /**
   * Hide the Checkbox label while allowing assistive devices to identify the control.
   */
  hideLabel?: boolean
  /**
   * @deprecated The Icon to display before or after the Checkbox label.
   */
  icon?: SystemIconKey | OliveImageIcon
  /**
   * @deprecated Position the supplied icon before or after the Checkbox label.
   */
  iconPosition?: IconPosition
  /**
   * A unique identifier for the input element.
   */
  id?: string
  /**
   * The indeterminate state of a Checkbox.
   */
  indeterminate?: boolean
  /**
   * Applies an 'invalid' visual treatment. Use the `error` prop instead.
   * @deprecated
   */
  invalid?: boolean
  /**
   * The label for the Checkbox.
   */
  label: string | React.ReactElement<TextEllipsisProps>
  /**
   * The name HTML attribute for the input.
   */
  name?: string
  /**
   * Accepts attributes matching the pattern on[A-Z].* in order to register event handlers.
   */
  'on[A-Z].*'?: React.EventHandler<React.SyntheticEvent<HTMLInputElement>>
  /**
   * The function to call when a 'change' event is fired.
   */
  onChange?: InputOnChangeHandler
  /**
   * Adds a visual treatment indicating that the associated control requires a value.
   */
  required?: boolean
  /**
   * The provided component will display at the start of the `Checkbox`.
   */
  startElement?: React.ReactElement
  /**
   * The value of the Checkbox.
   */
  value?: string
}

/**
 * Checkboxes let users select and deselect multiple items from a list and may also be used to turn an option on or off.
 */
export const Checkbox = ({
  checked = false,
  'data-qa': dataQa,
  description,
  disabled = false,
  endElement,
  error,
  forwardedRef,
  hideLabel = false,
  icon,
  iconPosition = 'beforeText',
  id,
  indeterminate = false,
  invalid = false,
  label,
  name,
  onChange,
  required = false,
  startElement,
  value,
  ...restProps
}: CheckboxProps) => {
  const formContext = useFormContext()
  const sx = useThemeStyles(styles)
  const [isFocused, setIsFocused] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const generatedCheckboxId = useUniqueId('short')
  const checkboxId = id || generatedCheckboxId

  const errorId = useUniqueId('short')
  const descriptionId = useUniqueId('short')

  let userSelectNoneClass = ''

  const labelStyles = [sx.default.label, (invalid || error) && sx.invalid.label]

  const labelTextStyles = [sx.default.text, hideLabel && sx.hideLabel.text]

  if (icon) {
    consoleWarn(
      `The "icon" and "iconPosition" props have been deprecated in favor of "startElement" and "endElement". Instead of passing a string for an icon kind, you should pass an icon React component from "@ds/icons" or the Image component from "@ds/ui" into the "startElement" or "endElement" prop.`,
    )
  }

  if (invalid) {
    deprecatedPropMessage({
      component: 'Checkbox',
      prop: 'invalid',
      newProp: 'error',
    })
  }

  const iconStyles = [sx.default.icon, iconPosition && sx[iconPosition].icon]

  const iconNode = icon && (
    <span css={iconStyles} data-qa={dataQa && `${dataQa}-icon`}>
      <IconOrImage kind={icon} />
    </span>
  )

  const startElementNode =
    (startElement && (
      <span
        css={[sx.default.icon, sx.beforeText.icon]}
        data-qa={dataQa && `${dataQa}-start`}
      >
        {startElement}
      </span>
    )) ||
    (icon && iconPosition === 'beforeText' && iconNode)

  const endElementNode =
    (endElement && (
      <span
        css={[sx.default.icon, sx.afterText.icon]}
        data-qa={dataQa && `${dataQa}-end`}
      >
        {endElement}
      </span>
    )) ||
    (icon && iconPosition === 'afterText' && iconNode)

  const requiredAsteriskNode = required && (
    <InputRequiredAsterisk data-qa={dataQa && `${dataQa}-required`} />
  )

  const descriptionNode = description && (
    <div css={sx.default.description}>
      <InputDescription
        id={descriptionId}
        kind="helper"
        disabled={disabled}
        data-qa={dataQa && `${dataQa}-description`}
      >
        {description as string}
      </InputDescription>
    </div>
  )

  const errorStyles = [
    sx.default.error,
    error && description && sx.default.descriptionError,
  ]

  const errorNode = error && (
    <div css={errorStyles}>
      <InputDescription
        id={errorId}
        kind="error"
        disabled={disabled}
        data-qa={dataQa && `${dataQa}-error`}
      >
        {error as string}
      </InputDescription>
    </div>
  )

  useEffect(() => {
    /**
     * The following prevents text selection when a user presses shift while a
     * Checkbox is focused.  The use case is to allow a user to check multiple
     * boxes.
     *
     * Note: The consumer needs to build the functionality, this only prevents
     * the text selection.
     *
     * 1. User checks a checkbox (which focuses it)
     * 2. User holds Shift key
     * 3. Component adds class to disable text selection
     * 4. User selects another checkbox
     * 5. App checks appropriate boxes -- see note
     * 6. User releases Shift key
     * 7. Component re-enables text selection
     */
    const allowTextSelectIfShift = (event: KeyboardEvent) => {
      if (event.key === keyboardEventKeys.Shift) {
        removeCssClass(userSelectNoneClass)
        document.removeEventListener('keyup', allowTextSelectIfShift, false)
      }
    }

    const preventTextSelectIfShift = (event: KeyboardEvent) => {
      if (event.key === keyboardEventKeys.Shift) {
        addCssClass(userSelectNoneClass)
        document.addEventListener('keyup', allowTextSelectIfShift, false)
      }
    }

    if (isFocused) {
      document.addEventListener('keydown', preventTextSelectIfShift, false)
    }

    return () => {
      document.removeEventListener('keydown', preventTextSelectIfShift, false)
      document.removeEventListener('keyup', allowTextSelectIfShift, false)
      removeCssClass(userSelectNoneClass)
    }
  }, [isFocused, userSelectNoneClass])

  const handleOnClick = (handler: MouseEventHandler) => {
    const wrappedHandler = (event: React.MouseEvent) => {
      event.persist()
      inputRef?.current?.focus()
      handler(event)
    }

    return wrappedHandler
  }

  useEffect(() => {
    inputRef.current!.indeterminate = !!indeterminate
  }, [indeterminate])

  const ariaDescribedByValue = [
    description && !error ? descriptionId : '',
    !description && error ? errorId : '',
    description && error ? `${descriptionId} ${errorId}` : '',
    restProps['aria-describedby'] || '',
  ]
    .join(' ')
    .trim()

  const additionalAttributes = {
    ...(ariaDescribedByValue && { 'aria-describedby': ariaDescribedByValue }),
    ...(invalid || error ? { 'aria-invalid': true } : {}),
  }

  return (
    <ClassNames>
      {({ css }) => {
        userSelectNoneClass = css(sx.userSelectNone)
        return (
          <div css={sx.default.wrap}>
            <input
              {...additionalAttributes}
              {...dataProps(restProps)}
              aria-required={
                formContext?.requiredAttribute
                  ? undefined
                  : required || undefined
              }
              checked={checked ?? false}
              css={sx.default.input}
              data-qa={dataQa}
              disabled={disabled}
              id={checkboxId}
              name={name}
              onBlur={() => setIsFocused(false)}
              onChange={onChange || (() => undefined)} // Adding the logical OR here to account for failing proptypes in unit test (see RadioButton)
              onFocus={() => setIsFocused(true)}
              ref={inputRef}
              required={formContext?.requiredAttribute ? required : undefined}
              type="checkbox"
              value={value}
            />

            {/* eslint-disable jsx-a11y/click-events-have-key-events */}
            {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
            <label
              {...onProps(restProps)}
              css={labelStyles}
              data-qa={dataQa && `${dataQa}-label`}
              htmlFor={checkboxId}
              onClick={
                restProps.onClick ? handleOnClick(restProps.onClick) : undefined
              }
              onMouseEnter={restProps.onMouseEnter}
              onMouseLeave={restProps.onMouseLeave}
              ref={forwardedRef}
            >
              {/* Using outer span to target focus indicator */}
              <span>
                <span
                  css={labelTextStyles}
                  data-qa={dataQa && `${dataQa}-label-text`}
                >
                  {startElementNode}
                  {label}
                  {requiredAsteriskNode}
                  {endElementNode}
                </span>
              </span>
            </label>
            {descriptionNode}
            {errorNode}
          </div>
        )
      }}
    </ClassNames>
  )
}

Checkbox.icons = checkboxIcons
Checkbox.iconPositions = checkboxIconPositions

Checkbox.propTypes = {
  checked: PropTypes.bool,
  'data-.*': PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  endElement: PropTypes.element,
  error: PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  hideLabel: PropTypes.bool,
  icon: PropTypes.oneOf(checkboxIcons),
  iconPosition: PropTypes.oneOf(checkboxIconPositions),
  id: PropTypes.string,
  indeterminate: PropTypes.bool,
  invalid: PropTypes.bool,
  label: PropTypes.node.isRequired,
  name: PropTypes.string,
  'on[A-Z].*': PropTypes.func,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  startElement: PropTypes.element,
  value: PropTypes.string,
}

Checkbox.displayName = 'Checkbox'
